import React, { useState } from 'react';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheck } from 'react-icons/fi';
import colors from '../../styles/colors';

const ContactSection = styled.section`
  padding: 120px 40px;
  background: ${colors.background.light};

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Subtitle = styled.div`
  color: ${colors.primary.main};
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
  font-weight: 800;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: ${colors.text.body};
  max-width: 550px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 50px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoColumn = styled.div``;

const InfoCard = styled.div`
  background: ${colors.background.white};
  padding: 40px;
  border-radius: 24px;
  box-shadow: ${colors.shadowLight};
  margin-bottom: 25px;
`;

const InfoTitle = styled.h3`
  font-size: 1.3rem;
  color: ${colors.text.heading};
  margin-bottom: 28px;
  font-weight: 700;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 22px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${colors.background.light};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary.main};
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const InfoText = styled.div`
  h4 {
    font-family: 'Quicksand', sans-serif;
    font-size: 1rem;
    color: ${colors.text.heading};
    margin-bottom: 5px;
    font-weight: 700;
  }
  p {
    font-family: 'Quicksand', sans-serif;
    font-size: 0.95rem;
    color: ${colors.text.body};
    line-height: 1.6;
  }
  a {
    font-family: 'Quicksand', sans-serif;
    color: ${colors.primary.main};
    font-weight: 600;
    text-decoration: none !important;
    &:hover {
      color: ${colors.accent.copper};
    }
  }
`;

const HoursCard = styled.div`
  background: ${colors.gradient.forest};
  padding: 30px;
  border-radius: 20px;
  color: ${colors.text.white};
`;

const HoursTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
`;

const HoursGrid = styled.div`
  display: grid;
  gap: 10px;
  font-size: 0.95rem;
`;

const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${colors.text.light};
`;

const FormColumn = styled.div``;

const Form = styled.form`
  background: ${colors.background.white};
  padding: 45px;
  border-radius: 24px;
  box-shadow: ${colors.shadowLight};
`;

const FormTitle = styled.h3`
  font-size: 1.4rem;
  color: ${colors.text.heading};
  margin-bottom: 30px;
  font-weight: 700;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.$full ? '20px' : '0'};
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${colors.text.heading};
  margin-bottom: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid ${colors.borderLight};
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  color: ${colors.text.dark};
  background: ${colors.background.white};
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }

  &::placeholder {
    color: ${colors.text.body};
    opacity: 0.6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid ${colors.borderLight};
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  color: ${colors.text.dark};
  background: ${colors.background.white};
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 15px 20px;
  border: 2px solid ${colors.borderLight};
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  color: ${colors.text.dark};
  background: ${colors.background.white};
  min-height: 130px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }

  &::placeholder {
    color: ${colors.text.body};
    opacity: 0.6;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px 36px;
  background: ${props => props.$success ? '#16a34a' : colors.gradient.gold};
  color: ${props => props.$success ? colors.text.white : colors.primary.dark};
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: ${props => props.$success ? 'none' : colors.shadowGold};
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
  }
`;

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <ContactSection id="contact">
      <Container>
        <Header>
          <Subtitle>Get In Touch</Subtitle>
          <Title>Contact Us Today</Title>
          <Description>
            Ready to transform your outdoor space? Get in touch for a free estimate.
          </Description>
        </Header>

        <Grid>
          <InfoColumn>
            <InfoCard>
              <InfoTitle>Contact Information</InfoTitle>
              <InfoItem>
                <InfoIcon><FiPhone /></InfoIcon>
                <InfoText>
                  <h4>Phone</h4>
                  <p><a href="tel:5559876543">(555) 987-6543</a></p>
                </InfoText>
              </InfoItem>
              <InfoItem>
                <InfoIcon><FiMail /></InfoIcon>
                <InfoText>
                  <h4>Email</h4>
                  <p><a href="mailto:hello@greenridgelandscaping.com">hello@greenridgelandscaping.com</a></p>
                </InfoText>
              </InfoItem>
              <InfoItem>
                <InfoIcon><FiMapPin /></InfoIcon>
                <InfoText>
                  <h4>Location</h4>
                  <p>742 Elm Street<br />Essex, VT 05452</p>
                </InfoText>
              </InfoItem>
            </InfoCard>

            <HoursCard>
              <HoursTitle><FiClock /> Business Hours</HoursTitle>
              <HoursGrid>
                <HoursRow><span>Monday - Friday</span><span>7:00 AM - 6:00 PM</span></HoursRow>
                <HoursRow><span>Saturday</span><span>8:00 AM - 4:00 PM</span></HoursRow>
                <HoursRow><span>Sunday</span><span>Closed</span></HoursRow>
              </HoursGrid>
            </HoursCard>
          </InfoColumn>

          <FormColumn>
            <Form onSubmit={handleSubmit}>
              <FormTitle>Request a Free Estimate</FormTitle>

              <FormRow>
                <FormGroup>
                  <Label>Your Name</Label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="(555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormGroup $full>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup $full>
                <Label>Service Needed</Label>
                <Select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a service...</option>
                  <option value="garden-center">Garden Center</option>
                  <option value="landscape">Landscape Design</option>
                  <option value="lawn">Lawn Maintenance</option>
                  <option value="snow">Snow Removal</option>
                  <option value="other">Other</option>
                </Select>
              </FormGroup>

              <FormGroup $full>
                <Label>Message</Label>
                <TextArea
                  name="message"
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={handleChange}
                />
              </FormGroup>

              <SubmitButton type="submit" $success={submitted}>
                {submitted ? (
                  <><FiCheck /> Message Sent!</>
                ) : (
                  <><FiSend /> Send Request</>
                )}
              </SubmitButton>
            </Form>
          </FormColumn>
        </Grid>
      </Container>
    </ContactSection>
  );
}
